import { useStrikeConfiguration } from '@communities/content-management/hooks';
import { ErrorBanner, SectionHeader } from '@core/components';
import { Badge, BadgeVariant, Card, IconButton, Edit, IconSize, Shimmer, Section, Text } from '@soteria/design-system-ve';
import { useState, useCallback } from 'react';

import { RollingWindowDialog } from './RollingWindowDialog';
import * as styles from './StrikeConfigSection.css';

const DEFAULT_ROLLING_WINDOW_DAYS = 30;

interface StrikeConfigSectionProps {
  communityId: string;
  isEntityAdmin: boolean;
}

/**
 * Section for strike configuration (rolling window).
 * Configures the time window over which strikes accumulate before resetting.
 */
export function StrikeConfigSection({ communityId, isEntityAdmin }: StrikeConfigSectionProps) {
  const { configuration, loading, errors, updateConfiguration } = useStrikeConfiguration(communityId);

  const [isRollingWindowDialogOpen, setIsRollingWindowDialogOpen] = useState(false);

  const isDefaultWindow = configuration.rollingWindowDays === DEFAULT_ROLLING_WINDOW_DAYS;
  const windowDisplayText = `${configuration.rollingWindowDays} days${isDefaultWindow ? ' (default)' : ''}`;

  const handleSaveRollingWindow = useCallback(
    async (days: number): Promise<boolean> =>
      await updateConfiguration({
        rollingWindowDays: days,
        labelStrikeMappings: configuration.labelStrikeMappings,
      }),
    [updateConfiguration, configuration.labelStrikeMappings]
  );

  const renderRollingWindowContent = () => {
    if (errors) {
      return <ErrorBanner errors={errors} />;
    }

    if (loading) {
      return <Shimmer className={styles.loading} />;
    }

    return (
      <div className={styles.container}>
        <div className={styles.settingsRow}>
          <div>
            <Text className={styles.label}>Rolling Window</Text>
            <Text className={styles.helperText}>Time period over which strikes accumulate before resetting</Text>
          </div>
          <div className={styles.valueContainer}>
            <Badge text={windowDisplayText} variant={BadgeVariant.Approved} />
            {isEntityAdmin && (
              <IconButton aria-label="Edit Rolling Window" icon={Edit} iconSize={IconSize.XSmall} onClick={() => setIsRollingWindowDialogOpen(true)} />
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Rolling Window Section */}
      <Section spacing="large">
        <SectionHeader description="Configure the time window over which strikes accumulate before resetting." title="Strike Configuration" />
        <Card>{renderRollingWindowContent()}</Card>
      </Section>

      {/* Rolling Window Edit Dialog */}
      <RollingWindowDialog
        communityId={communityId}
        currentDays={configuration.rollingWindowDays}
        errors={errors}
        isOpen={isRollingWindowDialogOpen}
        onClose={() => setIsRollingWindowDialogOpen(false)}
        onSave={handleSaveRollingWindow}
      />
    </>
  );
}
